import React, { useState, useCallback } from 'react';
import { createRsvp } from '../services/apiService.ts'; // Import createRsvp from apiService
import { DietaryPreference, RSVP } from '../types.ts';
import { CLOSING_TEXT, EVENT_TITLE } from '../constants.ts';

interface RSVPFormProps {
  onRSVPComplete: (rsvpData: RSVP) => void; // Modified to pass RSVP data
}

export const RSVPForm: React.FC<RSVPFormProps> = ({ onRSVPComplete }) => {
  const [name, setName] = useState<string>('');
  const [attending, setAttending] = useState<boolean | null>(null);
  const [dietaryPreference, setDietaryPreference] = useState<DietaryPreference | ''>('');
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateForm = useCallback(() => {
    if (!name.trim()) return 'Please enter your name.';
    if (attending === null) return 'Please indicate if you are attending.';
    if (attending && !dietaryPreference) return 'Please select your dietary preference.';
    return '';
  }, [name, attending, dietaryPreference]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      setIsLoading(false);
      return;
    }

    try {
      // We no longer generate ID client-side; the server will do it.
      const rsvpToSend: Omit<RSVP, 'id'> = {
        name,
        attending: attending!,
        dietaryPreference: attending ? (dietaryPreference as DietaryPreference) : DietaryPreference.N_A,
        timestamp: Date.now(),
      };
      
      const newRSVP = await createRsvp(rsvpToSend); // Send to API and get server-generated ID
      
      setSuccess('Thank you for your RSVP!');
      // Reset form fields after successful submission
      setName('');
      setAttending(null);
      setDietaryPreference('');
      onRSVPComplete(newRSVP); // Pass the new RSVP data
    } catch (err: any) {
      setError(err.message || 'Failed to submit RSVP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [name, attending, dietaryPreference, onRSVPComplete, validateForm]);

  return (
    <div className="p-4 md:p-8 bg-primary-bg rounded-lg font-montserrat w-full">
      <h2 className="text-3xl font-playfair font-bold text-center text-accent-gold mb-8">RSVP for the {EVENT_TITLE}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-text-white mb-1">Full Name</label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 block w-full px-4 py-2 border border-accent-gold rounded-md shadow-sm focus:ring-accent-gold focus:border-accent-gold sm:text-sm bg-primary-bg text-text-white placeholder-gray-500"
            placeholder="John Doe"
            required
            aria-required="true"
          />
        </div>

        <div>
          <span className="block text-sm font-medium text-text-white mb-2">Will you be attending?</span>
          <div className="flex space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="attending"
                value="yes"
                checked={attending === true}
                onChange={() => setAttending(true)}
                className="form-radio h-4 w-4 text-accent-gold border-accent-gold focus:ring-accent-gold bg-primary-bg"
                required
                aria-required="true"
              />
              <span className="ml-2 text-text-white">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="attending"
                value="no"
                checked={attending === false}
                onChange={() => setAttending(false)}
                className="form-radio h-4 w-4 text-accent-gold border-accent-gold focus:ring-accent-gold bg-primary-bg"
              />
              <span className="ml-2 text-text-white">No</span>
            </label>
          </div>
        </div>

        {attending && (
          <>
            <div>
              <label htmlFor="dietaryPreference" className="block text-sm font-medium text-text-white mb-1">Dietary Preference</label>
              <select
                id="dietaryPreference"
                value={dietaryPreference}
                onChange={(e) => setDietaryPreference(e.target.value as DietaryPreference | '')}
                className="mt-1 block w-full px-4 py-2 border border-accent-gold rounded-md shadow-sm focus:ring-accent-gold focus:border-accent-gold sm:text-sm bg-primary-bg text-text-white"
                required={attending} // This `required` attribute ensures selection if attending
                aria-required={attending}
              >
                <option value="" className="text-gray-500">Select an option</option>
                <option value={DietaryPreference.NO_SPECIAL_REQUIREMENTS}>No Special Requirements</option>
                <option value={DietaryPreference.VEGETARIAN}>Vegetarian</option>
                <option value={DietaryPreference.HALAL}>Halal</option>
              </select>
            </div>
          </>
        )}

        {error && (
          <div className="bg-red-900 border border-red-700 text-red-200 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Error!</strong>
            <span className="block sm:inline ml-2">{error}</span>
          </div>
        )}

        {success && (
          <div className="bg-green-900 border border-green-700 text-green-200 px-4 py-3 rounded relative" role="alert">
            <strong className="font-bold">Success!</strong>
            <span className="block sm:inline ml-2">{success}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-primary-bg bg-accent-gold hover:bg-opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Submitting...' : 'Submit RSVP'}
        </button>
      </form>

      {/* Closing message */}
      <div className="text-center mt-12 space-y-4">
        <p className="text-base text-text-white leading-relaxed mt-4" aria-label="Closing message">{CLOSING_TEXT}</p>
      </div>
    </div>
  );
};